import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowUpRight,
  CheckCircle2,
  Loader2,
  Sparkles,
  Target,
  XCircle,
} from "lucide-react";

import { Container } from "@/platform/layout/container";
import { Badge } from "@/platform/ui/badge";
import { Button } from "@/platform/ui/button";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";
import { cn } from "@/platform/lib/cn";
import { useSession } from "@/platform/auth/use-session";
import {
  fetchDemoQuestions,
  finishDiagnosisSession,
  recordDiagnosisAttempt,
  startDiagnosisSession,
} from "@/tenants/usp/features/diagnosis/api";

const ALT_LETTERS = ["A", "B", "C", "D", "E"] as const;

interface AnswerRecord {
  questionId: string;
  selected: string;
  isCorrect: boolean | null;
  discipline: string;
}

/**
 * `/usp/diagnostico` — demo público (sem sessão persistida).
 *
 * Mostra 10 questões reais do corpus Fuvest 2021-2025 balanceadas por
 * disciplina. Cada questão revela resposta correta + classificação
 * pedagógica ao clicar "Ver gabarito". CTA final → lista de espera.
 *
 * Quando Gate 1+2 destravarem e Fase M4 iniciar, esta rota será
 * substituída por diagnóstico real com sessão persistida em
 * usp_sessions + usp_attempts + scheduleReviews.
 */
export function UspDiagnosticoPage() {
  const { session } = useSession();
  const query = useQuery({
    queryKey: ["usp", "demo", "questions"],
    queryFn: fetchDemoQuestions,
    staleTime: 5 * 60_000,
  });

  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState<AnswerRecord[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const finishedRef = useRef(false);

  const questions = query.data ?? [];
  const current = questions[currentIdx];
  const currentAnswer = answers.find((a) => a.questionId === current?.id);
  const isFinished = answers.length === questions.length && questions.length > 0;

  // Logada: abre sessão persistida quando as questões carregam.
  useEffect(() => {
    if (!session || sessionId || questions.length === 0) return;
    void startDiagnosisSession(
      session.user.id,
      questions.map((q) => q.id),
    ).then((id) => setSessionId(id));
  }, [session, sessionId, questions]);

  // Logada: ao concluir, fecha sessão + agenda revisões das erradas.
  useEffect(() => {
    if (!isFinished || !session || !sessionId || finishedRef.current) return;
    finishedRef.current = true;
    const byDisc: Record<string, { total: number; correct: number }> = {};
    const wrong: string[] = [];
    for (const a of answers) {
      const b = byDisc[a.discipline] ?? { total: 0, correct: 0 };
      b.total++;
      if (a.isCorrect) b.correct++;
      else wrong.push(a.questionId);
      byDisc[a.discipline] = b;
    }
    const correct = answers.filter((a) => a.isCorrect).length;
    void finishDiagnosisSession(sessionId, session.user.id, byDisc, correct, wrong);
  }, [isFinished, session, sessionId, answers]);

  function selectAnswer(letter: string) {
    if (!current || currentAnswer) return;
    const isCorrect = current.correct_answer === letter;
    setAnswers((prev) => [
      ...prev,
      {
        questionId: current.id,
        selected: letter,
        isCorrect,
        discipline: current.discipline,
      },
    ]);
    // Logada: persiste a tentativa.
    if (session && sessionId) {
      void recordDiagnosisAttempt({
        userId: session.user.id,
        sessionId,
        questionId: current.id,
        selected: letter,
        correctAnswer: current.correct_answer,
      });
    }
  }

  function nextQuestion() {
    if (currentIdx < questions.length - 1) {
      setCurrentIdx((i) => i + 1);
    }
  }

  function reset() {
    setCurrentIdx(0);
    setAnswers([]);
    // Sem isto, a 2ª rodada reaproveita a sessão já finalizada: as tentativas
    // entram numa sessão com finished_at preenchido e a finalização não dispara.
    setSessionId(null);
    finishedRef.current = false;
  }

  if (query.isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="size-8 animate-spin text-[color:var(--color-muted-foreground)]" />
      </div>
    );
  }

  if (query.error || questions.length === 0) {
    return (
      <Container size="default" className="py-20">
        <p>Não foi possível carregar o demo. Tente em alguns minutos.</p>
      </Container>
    );
  }

  if (isFinished) {
    return <DemoSummary answers={answers} onRestart={reset} />;
  }

  if (!current) return null;

  return (
    <div className="relative overflow-hidden bg-mesh min-h-[calc(100vh-56px)]">
      <Blob color="sky" className="pointer-events-none absolute -left-32 -top-20 w-96 opacity-20 blur-2xl animate-blob" />
      <SoftStar color="mint" className="pointer-events-none absolute right-12 top-32 w-12 opacity-50 animate-float" />

      <Container size="default" className="relative py-12 md:py-16">
        <header className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <Link
            to="/usp"
            className="inline-flex items-center gap-1.5 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] transition-colors hover:text-[color:var(--color-foreground)]"
          >
            <ArrowLeft className="size-3.5" />
            FuvestPro USP
          </Link>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="gap-1.5">
              <Sparkles className="size-3" />
              Demo · sem cadastro
            </Badge>
            <Badge
              className="gap-1.5"
              style={{ backgroundColor: "var(--color-accent)", color: "white", border: "none" }}
            >
              {currentIdx + 1} / {questions.length}
            </Badge>
          </div>
        </header>

        {/* Progress bar */}
        <div className="mb-8 h-1.5 overflow-hidden rounded-full bg-[color:var(--color-muted)]">
          <div
            className="h-full transition-all duration-300"
            style={{
              width: `${((answers.length) / questions.length) * 100}%`,
              backgroundColor: "var(--color-accent)",
            }}
          />
        </div>

        <article className="rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-7 shadow-[var(--shadow-sm)] md:p-10">
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant="outline"
              className="gap-1.5"
              style={{ color: "var(--color-accent-deep)", borderColor: "hsl(222 65% 50% / 0.3)" }}
            >
              <Target className="size-3" />
              {current.discipline}
            </Badge>
            <span className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
              Fuvest {current.year} · Q{current.number}
            </span>
          </div>

          {current.has_base_text && current.base_text && (
            <blockquote className="mt-6 rounded-xl border-l-4 border-[--color-border] bg-[color:var(--color-muted)]/30 p-4 text-[length:var(--text-body-sm)] italic text-[color:var(--color-muted-foreground)] leading-relaxed">
              {current.base_text}
            </blockquote>
          )}

          <p className="mt-6 whitespace-pre-line text-[length:var(--text-body)] leading-relaxed">
            {current.stem}
          </p>

          {(current.has_image || current.has_graph || current.has_table) && current.pageImageUrl && (
            <details className="mt-5 rounded-xl border border-[--color-border] bg-[color:var(--color-muted)]/20 p-3">
              <summary className="cursor-pointer text-[length:var(--text-body-sm)] font-medium text-[color:var(--color-accent)]">
                Ver página original da prova (contém gráfico/imagem/tabela)
              </summary>
              <img
                src={current.pageImageUrl}
                alt={`Fuvest ${current.year} — página da questão ${current.number}`}
                loading="lazy"
                className="mt-3 w-full rounded-lg border border-[--color-border]"
              />
            </details>
          )}

          <ul className="mt-8 space-y-2.5" role="radiogroup" aria-label="Alternativas">
            {ALT_LETTERS.map((letter) => {
              const text = current.alternatives?.[letter];
              if (!text) return null;
              const isSelected = currentAnswer?.selected === letter;
              const isCorrectAnswer = current.correct_answer === letter;
              const revealed = !!currentAnswer;
              const showCorrect = revealed && isCorrectAnswer;
              const showWrong = revealed && isSelected && !isCorrectAnswer;

              return (
                <li key={letter}>
                  <button
                    type="button"
                    onClick={() => selectAnswer(letter)}
                    disabled={revealed}
                    role="radio"
                    aria-checked={isSelected}
                    className={cn(
                      "w-full text-left rounded-xl border-2 p-4 transition-colors duration-150",
                      !revealed && "border-[--color-border] hover:border-[--color-accent]/40 cursor-pointer",
                      revealed && !isSelected && !isCorrectAnswer && "border-[--color-border] opacity-60",
                      showCorrect && "border-[color:var(--color-success)] bg-[color:var(--color-success)]/10",
                      showWrong && "border-[color:var(--color-danger)] bg-[color:var(--color-danger)]/10",
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className={cn(
                          "mt-0.5 grid size-6 shrink-0 place-items-center rounded-full font-mono text-[length:var(--text-caption)] font-bold",
                          !revealed && "bg-[color:var(--color-muted)]",
                          showCorrect && "bg-[color:var(--color-success)] text-white",
                          showWrong && "bg-[color:var(--color-danger)] text-white",
                          revealed && !isSelected && !isCorrectAnswer && "bg-[color:var(--color-muted)]",
                        )}
                      >
                        {letter}
                      </span>
                      <span className="flex-1 text-[length:var(--text-body-sm)] leading-relaxed">
                        {text}
                      </span>
                      {showCorrect && <CheckCircle2 className="size-5 shrink-0" style={{ color: "var(--color-success)" }} />}
                      {showWrong && <XCircle className="size-5 shrink-0" style={{ color: "var(--color-danger)" }} />}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>

          {currentAnswer && (
            <div className="mt-7 rounded-xl border border-[--color-border] bg-[color:var(--color-muted)]/30 p-5">
              <p className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
                Classificação FuvestPro
              </p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {current.topic && (
                  <div>
                    <p className="text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">Tópico</p>
                    <p className="text-[length:var(--text-body-sm)] font-medium">{current.topic}</p>
                  </div>
                )}
                {current.cognitive_skill && (
                  <div>
                    <p className="text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">Habilidade</p>
                    <p className="text-[length:var(--text-body-sm)] font-medium">{current.cognitive_skill}</p>
                  </div>
                )}
                {current.question_type && (
                  <div>
                    <p className="text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">Tipo</p>
                    <p className="text-[length:var(--text-body-sm)] font-medium">{current.question_type}</p>
                  </div>
                )}
                {current.difficulty && (
                  <div>
                    <p className="text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">Dificuldade</p>
                    <p className="text-[length:var(--text-body-sm)] font-medium capitalize">{current.difficulty}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-end">
            {currentAnswer && currentIdx < questions.length - 1 && (
              <Button size="lg" variant="primary" onClick={nextQuestion}>
                Próxima questão
                <ArrowUpRight className="size-4" />
              </Button>
            )}
          </div>
        </article>

        <p className="mt-6 text-center text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
          Demo — sem persistência. Diagnóstico real com sessão calibrada estará disponível no beta.
        </p>
      </Container>
    </div>
  );
}

function DemoSummary({ answers, onRestart }: { answers: AnswerRecord[]; onRestart: () => void }) {
  const correct = answers.filter((a) => a.isCorrect).length;
  const total = answers.length;
  const pct = Math.round((correct / total) * 100);

  const byDiscipline = useMemo(() => {
    const map = new Map<string, { total: number; correct: number }>();
    for (const a of answers) {
      const bucket = map.get(a.discipline) ?? { total: 0, correct: 0 };
      bucket.total++;
      if (a.isCorrect) bucket.correct++;
      map.set(a.discipline, bucket);
    }
    return Array.from(map.entries())
      .map(([d, s]) => ({ discipline: d, ...s, pct: Math.round((s.correct / s.total) * 100) }))
      .sort((a, b) => a.pct - b.pct);
  }, [answers]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <div className="relative overflow-hidden bg-mesh min-h-[calc(100vh-56px)]">
      <Blob color="sky" className="pointer-events-none absolute -left-32 -top-20 w-96 opacity-25 blur-2xl animate-blob" />
      <SoftStar color="mint" className="pointer-events-none absolute right-12 top-24 w-14 opacity-60 animate-float" />

      <Container size="default" className="relative py-16 md:py-24">
        <div className="text-center animate-fade-up">
          <Badge
            className="gap-1.5"
            style={{ backgroundColor: "var(--color-accent)", color: "white", border: "none" }}
          >
            <CheckCircle2 className="size-3" />
            Demo concluído
          </Badge>
          <h1 className="mt-6 font-display text-[length:var(--text-display-lg)] font-semibold leading-tight tracking-[var(--text-display-lg--letter-spacing)] text-balance">
            Você acertou <span className="text-gradient-accent">{correct} de {total}</span>.
          </h1>
          <p className="mt-3 text-[length:var(--text-body-lg)] text-[color:var(--color-muted-foreground)]">
            {pct}% de acerto na amostra. Veja seu desempenho por disciplina.
          </p>
        </div>

        <section className="mt-12">
          <h2 className="font-display text-[length:var(--text-display-sm)] font-semibold mb-5">
            Por disciplina
          </h2>
          <ul className="space-y-2">
            {byDiscipline.map((d) => (
              <li
                key={d.discipline}
                className="grid grid-cols-[1fr_auto] items-center gap-3 rounded-xl border border-[--color-border] bg-[color:var(--color-card)] p-4"
              >
                <div className="flex items-center gap-3">
                  <span className="w-32 text-[length:var(--text-body-sm)] font-medium capitalize">
                    {d.discipline}
                  </span>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-[color:var(--color-muted)]">
                    <div
                      className="h-full"
                      style={{
                        width: `${d.pct}%`,
                        backgroundColor:
                          d.pct >= 70
                            ? "var(--color-success)"
                            : d.pct >= 40
                              ? "var(--color-warning)"
                              : "var(--color-danger)",
                      }}
                    />
                  </div>
                </div>
                <span
                  data-tabular="true"
                  className="text-[length:var(--text-body-sm)] font-medium tabular-nums"
                >
                  {d.correct}/{d.total}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12 rounded-3xl bg-[color:var(--color-card)] border border-[--color-border] p-8 md:p-12 text-center">
          <Badge variant="outline" className="w-fit mx-auto">
            FuvestPro USP · beta
          </Badge>
          <h2 className="mt-5 font-display text-[length:var(--text-display-sm)] font-semibold leading-tight">
            Diagnóstico real, calibrado por psicometria.
          </h2>
          <p className="mt-3 mx-auto max-w-xl text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)] leading-relaxed">
            No beta, o diagnóstico inclui 90 questões em formato real de prova, projeção de nota
            combinada 1ª+2ª fase com peso de Eng. Produção, e plano semanal adaptativo gerado
            automaticamente.
          </p>
          <div className="mt-7 flex flex-col items-stretch gap-3 sm:flex-row sm:justify-center sm:gap-4">
            <Button asChild size="lg" variant="primary">
              <Link to="/cadastro?tenant=usp">
                Entrar na lista de espera
                <ArrowUpRight className="size-4" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" onClick={onRestart}>
              Refazer demo
            </Button>
          </div>
        </section>
      </Container>
    </div>
  );
}
